import { useEffect, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { useDialog } from "@/context/DialogContext";
import { absUrl } from "@/lib/config";
import { OnboardingField } from "@/pages/Onboarding";
import {
  X, FileText, DoorOpen, Rocket, DownloadSimple, CheckCircle,
  Warning, Package, ArrowClockwise, Star,
} from "@phosphor-icons/react";

function Stars({ value }) {
  const n = Number(value) || 0;
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={14} weight={i <= n ? "fill" : "regular"} className={i <= n ? "text-[#F59E0B]" : "text-muted-foreground"} />
      ))}
    </div>
  );
}

export default function UserDocumentsModal({ userId, onClose, onDataChanged }) {
  const { can } = useCapabilities();
  const { confirm } = useDialog();
  const canManageOffboarding = can("manage_offboarding");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("onboarding");

  // Offboarding initiation form
  const [showInitForm, setShowInitForm] = useState(false);
  const [lastDay, setLastDay] = useState("");
  const [reason, setReason] = useState("");
  const [initiating, setInitiating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/users/${userId}/documents`);
      setData(r.data);
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (userId) load(); }, [userId]); // eslint-disable-line

  const initiate = async () => {
    if (!lastDay) return;
    setInitiating(true);
    try {
      await api.post(`/offboarding/${userId}/initiate`, { last_working_day: lastDay, reason });
      setShowInitForm(false); setLastDay(""); setReason("");
      await load();
      onDataChanged?.();
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setInitiating(false); }
  };

  const completeOffboarding = async () => {
    const ok = await confirm({
      title: "Mark offboarding as complete?",
      description: "The user's account will be disabled. Their data is retained for compliance.",
      confirmText: "Complete offboarding",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.post(`/offboarding/${userId}/complete`);
      await load(); onDataChanged?.();
    } catch (e) { setErr(formatAxiosError(e)); }
  };

  const cancelOffboarding = async () => {
    const ok = await confirm({
      title: "Cancel offboarding process?",
      description: "All entered offboarding data will be lost. This cannot be undone.",
      confirmText: "Cancel offboarding",
      cancelText: "Keep offboarding",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.post(`/offboarding/${userId}/cancel`);
      await load(); onDataChanged?.();
    } catch (e) { setErr(formatAxiosError(e)); }
  };

  if (!userId) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white border border-border w-full max-w-4xl max-h-[92vh] flex flex-col" data-testid="user-docs-modal">
        <div className="p-6 border-b border-border flex items-center justify-between shrink-0">
          <div>
            <div className="eyebrow flex items-center gap-2 mb-1">
              <FileText size={12} weight="bold" /> Employee documents
            </div>
            <h2 className="font-display text-2xl font-black tracking-tight">
              {data?.user?.name || "Loading…"}
            </h2>
            {data?.user && (
              <div className="text-xs text-muted-foreground mt-1">
                {data.user.email} • {data.user.department || "—"} • {data.user.position || "—"}
                {data.user.disabled && <span className="ml-2 bg-red-100 text-[#FF3B30] px-1.5 py-0.5 text-[10px] font-mono-mini">OFFBOARDED</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} className="border border-border p-2 hover:bg-muted"><X size={14} weight="bold" /></button>
        </div>

        <div className="border-b border-border px-6 flex items-center gap-1 shrink-0">
          <button
            onClick={() => setTab("onboarding")}
            className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${tab === "onboarding" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid="docs-tab-onboarding"
          >
            <Rocket size={12} weight="bold" /> Onboarding docs
          </button>
          <button
            onClick={() => setTab("offboarding")}
            className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${tab === "offboarding" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid="docs-tab-offboarding"
          >
            <DoorOpen size={12} weight="bold" /> Offboarding
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {err && <div className="mb-4 px-4 py-2 border border-[#FF3B30] bg-red-50 text-sm text-[#FF3B30]">{err}</div>}
          {!loading && data && tab === "onboarding" && <OnboardingView data={data} />}
          {!loading && data && tab === "offboarding" && (
            <OffboardingView
              data={data}
              canManage={canManageOffboarding}
              showInitForm={showInitForm}
              setShowInitForm={setShowInitForm}
              lastDay={lastDay} setLastDay={setLastDay}
              reason={reason} setReason={setReason}
              onInitiate={initiate} initiating={initiating}
              onComplete={completeOffboarding}
              onCancel={cancelOffboarding}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function OnboardingView({ data }) {
  const cfg = data.onboarding_config;
  const sub = data.onboarding;
  if (!sub) {
    return (
      <div className="text-sm text-muted-foreground">
        This user hasn&apos;t started their onboarding yet.
      </div>
    );
  }
  const values = sub.values || {};
  const filesMeta = sub.files_meta || {};

  const sectionsSorted = (cfg.sections || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const itemsByS = {};
  (cfg.items || []).forEach(it => {
    (itemsByS[it.section_id] = itemsByS[it.section_id] || []).push(it);
  });
  Object.values(itemsByS).forEach(arr => arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));

  return (
    <div>
      {/* Progress summary */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="font-semibold">
            {sub.status === "complete"
              ? <span className="text-[#22C55E] flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Onboarding complete</span>
              : `${sub.progress.completed_required} / ${sub.progress.total_required} required items complete`}
          </span>
          <span className="font-mono-mini text-[10px] text-muted-foreground">{sub.progress.percent}%</span>
        </div>
        <div className="h-2 bg-muted overflow-hidden">
          <div className="h-full bg-[#002FA7]" style={{ width: `${sub.progress.required_percent}%` }} />
        </div>
      </div>

      {/* Sections */}
      {sectionsSorted.map(s => (
        <section key={s.id} className="mb-6 border border-border p-4">
          <h3 className="font-display text-lg font-black mb-3">{s.title}</h3>
          <div className="space-y-3">
            {(itemsByS[s.id] || []).map(it => (
              <div key={it.key}>
                <div className="eyebrow mb-1">{it.label}</div>
                <ReadOnlyValue item={it} value={values[it.key]} filesMeta={filesMeta} />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ReadOnlyValue({ item, value, filesMeta }) {
  if (value === undefined || value === null || value === "") {
    return <div className="text-xs text-muted-foreground italic">Not provided</div>;
  }
  if (item.type === "acknowledgment") {
    return value ? (
      <div className="flex items-center gap-1 text-sm text-[#22C55E]"><CheckCircle size={12} weight="fill" /> Acknowledged</div>
    ) : <div className="text-xs text-muted-foreground">Not acknowledged</div>;
  }
  if (item.type === "file") {
    const meta = filesMeta[value] || { id: value, filename: "file" };
    return (
      <a href={absUrl(`/api/files/${meta.id}`)} target="_blank" rel="noreferrer" className="border border-border px-2 py-1 text-xs hover:bg-muted inline-flex items-center gap-1">
        <DownloadSimple size={11} weight="bold" /> {meta.filename}
      </a>
    );
  }
  if (item.type === "multi_file") {
    return (
      <div className="flex flex-wrap gap-1">
        {(value || []).map(fid => {
          const meta = filesMeta[fid] || { id: fid, filename: "file" };
          return (
            <a key={fid} href={absUrl(`/api/files/${fid}`)} target="_blank" rel="noreferrer" className="border border-border px-2 py-1 text-xs hover:bg-muted inline-flex items-center gap-1">
              <DownloadSimple size={11} weight="bold" /> {meta.filename}
            </a>
          );
        })}
      </div>
    );
  }
  if (item.type === "address") {
    const v = value || {};
    const parts = [v.street, v.apartment, v.city, v.state, v.zip, v.country].filter(Boolean);
    return <div className="text-sm">{parts.join(", ") || <span className="text-xs text-muted-foreground italic">Not provided</span>}</div>;
  }
  if (item.type === "select") {
    const opt = (item.options || []).find(o => o.value === value);
    return <div className="text-sm">{opt?.label || value}</div>;
  }
  return <div className="text-sm whitespace-pre-wrap">{String(value)}</div>;
}

function OffboardingView({ data, canManage, showInitForm, setShowInitForm, lastDay, setLastDay, reason, setReason, onInitiate, initiating, onComplete, onCancel }) {
  const rec = data.offboarding;
  const cfg = data.offboarding_config;
  const history = data.offboarding_history || [];
  const userDisabled = !!data.user?.disabled;

  // "Initiate" section — shown when either there is no current record,
  // OR the current record is only history because the user was re-enabled
  // (i.e. their previous offboarding is complete but they came back).
  const showInitiateBlock = !rec || (rec.status === "complete" && !userDisabled);

  const prevHistoryEl = history.length > 0 ? (
    <section className="mt-8 border-t border-border pt-6">
      <div className="eyebrow mb-3 flex items-center gap-1">
        <ArrowClockwise size={12} weight="bold" /> Previous offboarding cycles ({history.length})
      </div>
      <div className="space-y-2">
        {history.map(h => (
          <div key={h.id} className="border border-border p-3 flex items-start justify-between text-sm">
            <div>
              <div className="font-semibold">
                Last working day: {h.last_working_day}
                <span className={`ml-2 text-[10px] font-mono-mini px-1.5 py-0.5 uppercase ${h.status === "complete" ? "bg-[#22C55E] text-white" : "bg-muted"}`}>
                  {h.status}
                </span>
              </div>
              {h.reason && <div className="text-xs text-muted-foreground mt-0.5">Reason: {h.reason}</div>}
              {h.initiator && <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">Initiated by {h.initiator.name} on {h.initiated_at?.slice(0, 10)}{h.completed_at ? `, completed ${h.completed_at.slice(0, 10)}` : ""}</div>}
            </div>
          </div>
        ))}
      </div>
    </section>
  ) : null;

  if (showInitiateBlock) {
    if (!canManage) {
      return (
        <>
          <div className="text-sm text-muted-foreground">No active offboarding record for this user.</div>
          {prevHistoryEl}
        </>
      );
    }
    return (
      <div>
        {rec && rec.status === "complete" && !userDisabled && (
          <div className="mb-4 border-l-4 border-l-[#F59E0B] bg-amber-50 p-3 text-sm">
            <div className="font-semibold text-amber-900 mb-1">This user was previously offboarded.</div>
            <div className="text-xs text-amber-900">
              They were offboarded on {rec.completed_at?.slice(0, 10) || rec.last_working_day} and have since been re-enabled.
              You can start a new offboarding cycle at any time.
            </div>
          </div>
        )}
        <p className="text-sm text-muted-foreground mb-4">
          This user is not being offboarded yet. To initiate their offboarding process, set their last
          working day and reason.
        </p>
        {!showInitForm ? (
          <button onClick={() => setShowInitForm(true)} className="btn-primary flex items-center gap-2" data-testid="start-offboarding">
            <DoorOpen size={14} weight="bold" /> Initiate offboarding
          </button>
        ) : (
          <div className="border border-border p-4 space-y-3">
            <div>
              <label className="eyebrow block mb-1">Last working day *</label>
              <input type="date" value={lastDay} onChange={e => setLastDay(e.target.value)} className="w-full border border-border px-3 py-2 text-sm" data-testid="init-last-day" />
            </div>
            <div>
              <label className="eyebrow block mb-1">Reason (optional)</label>
              <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Resignation, End of contract" className="w-full border border-border px-3 py-2 text-sm" data-testid="init-reason" />
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setShowInitForm(false)} className="border border-border px-3 py-2 text-sm hover:bg-muted">Cancel</button>
              <button onClick={onInitiate} disabled={!lastDay || initiating} className="btn-primary flex items-center gap-2 disabled:opacity-40" data-testid="confirm-initiate">
                <DoorOpen size={14} weight="bold" /> {initiating ? "Initiating…" : "Start offboarding"}
              </button>
            </div>
          </div>
        )}
        {prevHistoryEl}
      </div>
    );
  }

  const isComplete = rec.status === "complete";

  return (
    <div>
      {/* Header */}
      <div className="mb-6 border-l-4 border-l-[#F59E0B] pl-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-sm">Last working day: {rec.last_working_day}</span>
          <span className={`text-[10px] font-mono-mini px-1.5 py-0.5 ${isComplete ? "bg-[#22C55E] text-white" : "bg-[#F59E0B] text-white"}`}>
            {rec.status.toUpperCase()}
          </span>
        </div>
        {rec.reason && <div className="text-xs text-muted-foreground">Reason: {rec.reason}</div>}
        {rec.initiator && <div className="text-xs text-muted-foreground">Initiated by {rec.initiator.name} on {rec.initiated_at?.slice(0, 10)}</div>}
      </div>

      {/* Assets */}
      <section className="mb-6 border border-border p-4">
        <div className="eyebrow mb-2 flex items-center gap-1"><Package size={12} weight="bold" /> Asset return status</div>
        {rec.assets?.length ? (
          <div className="space-y-2">
            {rec.assets.map(a => {
              const s = rec.asset_return?.[a.id] || "pending";
              const colors = { returned: "bg-[#22C55E] text-white", pending: "bg-[#F59E0B] text-white", lost: "bg-[#FF3B30] text-white", na: "bg-muted text-foreground" };
              return (
                <div key={a.id} className="flex items-center justify-between text-sm">
                  <span>{a.name} <span className="text-[10px] font-mono-mini text-muted-foreground ml-2">{a.category}</span></span>
                  <span className={`text-[10px] font-mono-mini px-1.5 py-0.5 uppercase ${colors[s] || "bg-muted"}`}>{s}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">No assets were assigned to this user.</div>
        )}
      </section>

      {/* Handover notes */}
      <section className="mb-6 border border-border p-4">
        <div className="eyebrow mb-2">Handover notes</div>
        {rec.handover_notes ? (
          <div className="text-sm whitespace-pre-wrap">{rec.handover_notes}</div>
        ) : (
          <div className="text-xs text-muted-foreground italic">Not provided yet.</div>
        )}
      </section>

      {/* Exit interview */}
      <section className="mb-6 border border-border p-4">
        <div className="eyebrow mb-3">Exit interview</div>
        <div className="space-y-3">
          {(cfg.exit_questions || []).map(q => {
            const v = rec.exit_interview?.[q.key];
            return (
              <div key={q.key}>
                <div className="text-xs font-semibold mb-1">{q.label}</div>
                {q.type === "rating" ? <Stars value={v} /> : (
                  <div className="text-sm">
                    {v ? (q.type === "select" ? (q.options?.find(o => o.value === v)?.label || v) : v) : <span className="text-xs text-muted-foreground italic">Not answered</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Resignation */}
      {rec.resignation_letter_meta && (
        <section className="mb-6 border border-border p-4">
          <div className="eyebrow mb-2">Resignation letter</div>
          <a href={absUrl(`/api/files/${rec.resignation_letter_file_id}`)} target="_blank" rel="noreferrer" className="border border-border px-3 py-2 text-sm hover:bg-muted inline-flex items-center gap-1">
            <DownloadSimple size={12} weight="bold" /> {rec.resignation_letter_meta.filename}
          </a>
        </section>
      )}

      {/* Admin actions */}
      {canManage && !isComplete && (
        <div className="flex items-center justify-end gap-2 pt-4 border-t border-border">
          <button onClick={onCancel} className="border border-[#FF3B30] text-[#FF3B30] px-3 py-2 text-sm hover:bg-red-50 flex items-center gap-2" data-testid="cancel-offboarding">
            <ArrowClockwise size={14} weight="bold" /> Cancel offboarding
          </button>
          <button onClick={onComplete} className="btn-primary flex items-center gap-2" data-testid="complete-offboarding">
            <CheckCircle size={14} weight="bold" /> Complete offboarding
          </button>
        </div>
      )}

      {isComplete && (
        <div className="border border-[#22C55E] bg-green-50 text-[#166534] p-3 text-sm flex items-center gap-2">
          <CheckCircle size={14} weight="fill" /> Completed on {rec.completed_at?.slice(0, 10)}. The user&apos;s account has been disabled.
        </div>
      )}

      {prevHistoryEl}
    </div>
  );
}
